import { api } from './api';
import {
  RequestCase,
  ReceiveCase,
  LoadCaseFailure,
  ReceiveCasePermissions,
  ChildItemSelection,
  RequestCaseRiskMatrixDefinition,
  ReceiveCaseRiskMatrixDefinition,
  ChangeViewMode
} from './action-types';
import {
  LoggedError,
  errorSafe
} from 'app/infrastructure/redux';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { CompositeKeys, RenderMode, StoreItemPath } from 'app/types-shared';
import * as navigate from 'app/area/shared/navigation';
import { caseTypeId as getCaseTypeId, caseContextId as getCaseContextId } from 'app/infrastructure/data/wireformat';
import { getCaseId } from 'app/infrastructure/store';
import { SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { caseTranslations, viewModeCacheKey } from 'app/area/case/constants';
import { createDescriptionSelector } from 'app/infrastructure/translate/index';
import { routeFromStoreItemPath, sectionFromStoreItemPath } from 'app/infrastructure/store/entity-route';
import { mapRiskMatrixDefinitionToStoreFormat } from 'app/area/case/features/risk-matrix/risk-matrix-definition';
import { ViewMode } from 'app/area/case/types-store';
import { clientCache } from 'app/infrastructure/universal';
import { actionCreators as responseCachingActionCreators } from 'app/area/shared/response-caching/actions';
import { getItemsAfterKey, getItemsBeforeKey, isCaseViewModalOpen } from 'app/components/helpers';

const getCaseNo = (state: ApplicationState) => {
  const areaState = state.caseArea;
  const draftId = areaState.selectedId;
  return getCaseId(areaState.entities[draftId]);
};

const entityPathRoute = (entityPath: StoreItemPath[]) => {

  const exceptTblTrans = s => Object.keys(s)[0] !== 'tblTrans';
  const makePath = (aggr, curr) => `${aggr}/${Object.keys(curr)[0]}/${curr[Object.keys(curr)[0]]}`;

  return !entityPath ? undefined : entityPath.filter(exceptTblTrans).reduce(makePath, '');
};

export const actionCreators = {
  /**
   * Get risk matrix-definition for a case type.
   */
  caseRiskMatrixDefinition: (caseTypeId: number): ActionDispatching<any, any> => errorSafe(
    async (dispatch: any, getState: any) => {
      dispatch(new RequestCaseRiskMatrixDefinition(caseTypeId));

      //TODO: Implement support for clearing and refetching outdated risk matrix-definitions based on eTag.
      const riskDefState = getState().caseArea.riskMatrixDefinitions[caseTypeId];
      const riskMatrixDef = (riskDefState && !riskDefState.fetching && riskDefState.definition) ||
        await api.caseRiskMatrixDefinition(caseTypeId);

      dispatch(new ReceiveCaseRiskMatrixDefinition(caseTypeId, mapRiskMatrixDefinitionToStoreFormat(riskMatrixDef)));
    }),
  /**
   * Edit case - right now, passed down to edit component
   */
  editDocument: (): ActionDispatching<any, ApplicationState> => async (
    dispatch: any,
    getState: any
  ) => {
    const caseNo = getCaseNo(getState());
    isCaseViewModalOpen() ? navigate.toCaseEditInModal(caseNo) : navigate.toCaseEdit(caseNo);
  },
  /**
   * Edit case - right now, passed down to edit component
   */
  editDocumentItem: (entityPath: StoreItemPath[]): ActionDispatching<any, ApplicationState> => async (
    dispatch: any,
    getState: any
  ) => {
    const caseNo = getCaseNo(getState());
    isCaseViewModalOpen() ? navigate.toCaseEditInModal(caseNo, entityPathRoute(entityPath)) : 
      navigate.toCaseEdit(caseNo, entityPathRoute(entityPath));
  },
  /**
   * Loads **read data** for the given case
   * Once data is available, the UI definition is retreived from cache or server
   */
  loadCase: (
    caseNumber: number
  ): ActionDispatching<any, ApplicationState> => async (
    dispatch: any,
    getState: any
  ) => {
      try {

        dispatch(new RequestCase(caseNumber));

        const [caseData, casePermissions] = await Promise.all([
          api.case(caseNumber),
          api.casePermissions(caseNumber) 
        ]);

        if (!caseData) {
          dispatch(new LoadCaseFailure());
          return;
        }

        const caseType = getCaseTypeId(caseData);
        const caseContextId = getCaseContextId(caseData);
        
        dispatch(actionCreators.caseRiskMatrixDefinition(caseType));
        const uiDef = await dispatch(responseCachingActionCreators.getUiDefinition(caseType, caseContextId));

        if (!uiDef) {
          dispatch(new LoadCaseFailure());
          return;
        }

        dispatch(new ReceiveCasePermissions(caseNumber, {
          ...casePermissions
        }));
        dispatch(new ReceiveCase(caseNumber, caseData, { ...uiDef, applicationId: caseType }));

      } catch (err) {
        dispatch(new LoggedError(err));
        dispatch(new LoadCaseFailure());
      }
    },
  /**
   * Explicitly deleting a case
   */
  deleteDocument: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const state = getState().caseArea;
      const caseNo = getCaseNo(getState());
      const translate = createDescriptionSelector(state.texts);

      !isCaseViewModalOpen() && navigate.toStartupView();
      const result = (await api.deleteCase(caseNo)) as any;
      if (result && result.status === 204) {
        dispatch(
          new SuccessNotification(translate(caseTranslations.message_case_delete_success))
        );
      }
    }),
  /**
   * Duplicate case
   */
  duplicateDocument: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const caseNo = getCaseNo(getState());
      isCaseViewModalOpen() ? navigate.toCaseDuplicateInModal(caseNo) : navigate.toCaseDuplicate(caseNo);
    }),

  /**
   * Navigate to bowtie
   */
  loadBowtie: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const caseNo = getCaseNo(getState());
      navigate.toBowtie(caseNo);
    }),

  changeViewMode: (mode: ViewMode): ActionDispatching<any, any> =>
    errorSafe((dispatch: any, getState: any) => {
      clientCache.setItem(viewModeCacheKey, mode);
      dispatch(new ChangeViewMode(mode));
    }),

  /**
   * An item in a child table of a case is selected by the user.
   * Ensure this is reflected in the state and routing
   */
  selectDocumentItem: (childItem: CompositeKeys): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      const { activeChildItem, entities, selectedId, renderMode, uiDefinition } = getState().caseArea;

      const isNavigatingFromChild = Array.isArray(activeChildItem);

      dispatch(new ChildItemSelection(childItem));
          
      const itemsBeforeTrans = getItemsBeforeKey(childItem, 'tblTrans');
      const itemsAfterTrans = getItemsAfterKey(childItem, 'tblTrans');

      const casePathPrefix = document.location.pathname.split('/case')?.[0];
      const appendCasePathPrefix = !itemsBeforeTrans.length && isCaseViewModalOpen() && !!casePathPrefix.length;
      const parentRoute = appendCasePathPrefix ? casePathPrefix : '';
      
      // Use case number instead of id for the read mode route
      const currentCase = [{ tblTrans: getCaseId(entities[selectedId]) }];
      
      const childOfCase = childItem && [
        ...itemsBeforeTrans,
        ...currentCase,
        ...itemsAfterTrans
      ];
      const section = sectionFromStoreItemPath(childOfCase, renderMode, uiDefinition);

      navigate.toChildItem(routeFromStoreItemPath(
        childOfCase || currentCase, RenderMode.read),
        section && section.id,
        isNavigatingFromChild,
        parentRoute
      );
    })
};
